import { useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, Pencil, Trash2, Loader2, Eye, CopyPlus, CalendarRange, FileText, Landmark } from "lucide-react";
import { formatDate } from "@/lib/contact";
import { useAuth } from "@/context/AuthContext";
import { Checkbox } from "@/components/ui/checkbox";

function Cell({ value, type }) {
  if (value === null || value === undefined || value === "") return <span className="text-muted-foreground/50">—</span>;
  if (type === "boolean") return <span className={value ? "text-emerald-400" : "text-muted-foreground"}>{value ? "Yes" : "No"}</span>;
  if (type === "binary") return <span className="font-mono text-xs text-muted-foreground">binary</span>;
  if (type === "number") return <span className="font-mono">{value}</span>;
  if (type === "date") return <span className="font-mono text-xs">{formatDate(value)}</span>;
  return <span title={String(value)}>{String(value)}</span>;
}

export default function DataGrid({ columns, records, loading, sort, onSort, onEdit, onDelete, onView, onRename, onCopy, offset, selected, onToggle, onTogglePage, onDateFilter, dateField, scanCounts, qboSynced, readOnly, editable, onCellSave }) {
  const { isAdmin } = useAuth();
  const pageSel = records.filter((r) => selected.has(r.id)).length;
  const allSel = records.length > 0 && pageSel === records.length;
  const [active, setActive] = useState(null); // { rid, col }
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const start = (r, c) => {
    const v = r.data[c.name];
    setDraft(c.type === "date" && v ? String(v).slice(0, 10) : (v ?? ""));
    setActive({ rid: r.id, col: c.name });
  };
  const commit = async (r, c) => {
    if (String(r.data[c.name] ?? "") === String(draft ?? "")) { setActive(null); return; }
    setSaving(true);
    try { await onCellSave(r, c.name, draft); setActive(null); }
    catch { /* leave edit open on failure */ }
    finally { setSaving(false); }
  };
  return (
    <div className="border rounded-lg bg-card overflow-auto max-h-[62vh] relative" data-testid="data-grid">
      {loading && (
        <div className="absolute inset-0 bg-background/40 flex items-center justify-center z-20"><Loader2 className="animate-spin text-primary" /></div>
      )}
      <table className="w-full text-sm border-collapse">
        <thead className="sticky top-0 z-10 bg-secondary">
          <tr>
            <th className="pl-3 py-2.5 w-8">{!readOnly && <Checkbox className="data-[state=indeterminate]:bg-primary/40" checked={allSel ? true : pageSel ? "indeterminate" : false} onCheckedChange={() => onTogglePage(!allSel)} disabled={!records.length} data-testid="record-select-all" aria-label="Select all on this page" />}</th>
            <th className="px-3 py-2.5 text-left font-mono text-[10px] text-muted-foreground w-10">#</th>
            {scanCounts && <th className="px-3 py-2.5 text-left font-medium whitespace-nowrap" data-testid="scans-column-header">Scans</th>}
            {qboSynced && <th className="px-3 py-2.5 text-left font-medium whitespace-nowrap" data-testid="qbo-column-header">QuickBooks</th>}
            {columns.map((c) => (
              <th key={c.name} className="group/th px-3 py-2.5 text-left whitespace-nowrap">
                <button onClick={() => onSort(c.name)} data-testid={`sort-${c.name.replace(/\s+/g, "-")}`}
                  className="inline-flex items-center gap-1.5 font-medium hover:text-primary transition-colors">
                  {c.name}
                  {sort.col === c.name ? (sort.order === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="opacity-30" />}
                </button>
                {isAdmin && !readOnly && <button onClick={() => onRename(c.name)} aria-label={`Rename ${c.name}`} data-testid={`rename-${c.name.replace(/\s+/g, "-")}`}
                  className="ml-1.5 align-middle text-muted-foreground opacity-0 group-hover/th:opacity-100 focus:opacity-100 hover:text-primary transition-opacity">
                  <Pencil size={11} />
                </button>}
                <button onClick={() => onDateFilter(c.name)} aria-label={`Filter by ${c.name} date`} title="Use as date filter" data-testid={`date-filter-col-${c.name.replace(/\s+/g, "-")}`}
                  className={`ml-1.5 align-middle transition-opacity hover:text-primary ${dateField === c.name ? "text-primary opacity-100" : "text-muted-foreground opacity-0 group-hover/th:opacity-100 focus:opacity-100"}`}>
                  <CalendarRange size={12} />
                </button>
              </th>
            ))}
            <th className="px-3 py-2.5 sticky right-0 bg-secondary w-20" />
          </tr>
        </thead>
        <tbody>
          {records.map((r, i) => (
            <tr key={r.id} className={`border-t hover:bg-secondary/40 transition-colors group ${selected.has(r.id) ? "bg-primary/10" : ""}`} data-testid={`table-row-${i}`}>
              <td className="pl-3 py-2">{!readOnly && <Checkbox checked={selected.has(r.id)} onCheckedChange={() => onToggle(r.id)} data-testid={`record-select-${i}`} aria-label={`Select row ${offset + i + 1}`} />}</td>
              <td className="px-3 py-2 font-mono text-[10px] text-muted-foreground">{offset + i + 1}</td>
              {scanCounts && <td className="px-3 py-2">
                <button onClick={() => onView(r)} data-testid={`scans-cell-${i}`} title="Open this customer's scans"
                  className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-mono transition-colors ${scanCounts[r.id] ? "bg-cyan-500/15 text-cyan-400 hover:bg-cyan-500/25" : "text-muted-foreground hover:text-primary"}`}>
                  <FileText size={12} /> {scanCounts[r.id] || 0}
                </button>
              </td>}
              {qboSynced && <td className="px-3 py-2" data-testid={`qbo-cell-${i}`}>
                {qboSynced[r.id]
                  ? <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium bg-sky-500/15 text-sky-400" data-testid={`qbo-synced-${i}`}><Landmark size={12} /> ✓ in QuickBooks</span>
                  : <span className="text-xs text-muted-foreground">—</span>}
              </td>}
              {columns.map((c) => {
                const isActive = active && active.rid === r.id && active.col === c.name;
                const canEdit = editable && c.type !== "binary";
                const tid = `cell-${i}-${c.name.replace(/\s+/g, "-")}`;
                if (c.type === "boolean" && editable) {
                  return (
                    <td key={c.name} className="px-3 py-2">
                      <button type="button" onClick={() => onCellSave(r, c.name, !r.data[c.name])} data-testid={tid}
                        className={`text-sm hover:underline ${r.data[c.name] ? "text-emerald-400" : "text-muted-foreground"}`}>
                        {r.data[c.name] ? "Yes" : "No"}
                      </button>
                    </td>
                  );
                }
                return (
                  <td key={c.name} className={`px-3 py-2 max-w-[260px] ${isActive ? "" : "truncate"} ${canEdit && !isActive ? "cursor-text hover:bg-primary/5" : ""}`}
                    onClick={canEdit && !isActive ? () => start(r, c) : undefined} data-testid={tid} title={canEdit ? "Click to edit" : undefined}>
                    {isActive ? (
                      <input autoFocus disabled={saving} step="any"
                        type={c.type === "number" ? "number" : c.type === "date" ? "date" : "text"}
                        value={draft ?? ""} onChange={(e) => setDraft(e.target.value)} onBlur={() => commit(r, c)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(r, c); } else if (e.key === "Escape") { setActive(null); } }}
                        className="w-full bg-background border border-primary rounded px-1.5 py-1 text-sm outline-none"
                        data-testid={`cell-input-${i}-${c.name.replace(/\s+/g, "-")}`} />
                    ) : (
                      <Cell value={r.data[c.name]} type={c.type} />
                    )}
                  </td>
                );
              })}
              <td className="px-3 py-2 sticky right-0 bg-card group-hover:bg-secondary transition-colors">
                <div className="flex gap-2 justify-end">
                  {!readOnly && <><button onClick={() => onCopy(r)} className="text-muted-foreground hover:text-cyan-400" data-testid={`copy-record-${i}`} aria-label="Copy to another database" title="Copy to another database"><CopyPlus size={14} /></button>
                  <button onClick={() => onView(r)} className="text-muted-foreground hover:text-cyan-400" data-testid={`view-record-${i}`} aria-label="Open card"><Eye size={14} /></button>
                  <button onClick={() => onEdit(r)} className="text-muted-foreground hover:text-primary" data-testid={`edit-record-${i}`} aria-label="Edit"><Pencil size={14} /></button>
                  <button onClick={() => onDelete(r)} className="text-muted-foreground hover:text-red-400" data-testid={`delete-record-${i}`} aria-label="Delete"><Trash2 size={14} /></button></>}
                </div>
              </td>
            </tr>
          ))}
          {!loading && records.length === 0 && (
            <tr><td colSpan={columns.length + (scanCounts ? 4 : 3) + (qboSynced ? 1 : 0)} className="py-10 text-muted-foreground" data-testid="grid-empty"><span className="sticky left-6 inline-block px-4">No records found.</span></td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
